import { setSpaCredentials } from '@src/utility/spaCredentials';

/**
 * Fetches the DatoCMS read token and OpenAI key from the arbor-v1
 * backend and populates the `spaCredentials` module holder (PLAT-9905).
 *
 * Previously these were `REACT_APP_*` env vars baked into the public
 * bundle; the rotation moves them behind Clerk auth without otherwise
 * changing the call shape at the use sites (createGQLClient, new OpenAI).
 *
 * Called from `useStoreInitialization` after `setClerkContext` and
 * before `datoClientStore.initializeClients()` (which synchronously
 * reads the holder via the `gqlAuthToken` getter).
 *
 * Throws on dato-token failure (required for the GQL client). The
 * OpenAI key endpoint is rolling out separately; if it is missing or
 * fails we warn and populate an empty string so the editor still loads
 * — AI features will surface their own errors when invoked.
 */
export async function fetchSpaCredentials(
  clerkToken: string,
  apiUrl: string,
): Promise<void> {
  const headers = { authorization: `Bearer ${clerkToken}` };
  const [datoRes, openaiRes] = await Promise.all([
    fetch(`${apiUrl}/api/dato/subscription-token`, { headers }),
    fetch(`${apiUrl}/api/aiprompts/openai-key`, { headers }).catch(() => null),
  ]);
  if (!datoRes.ok) {
    throw new Error(
      `fetchSpaCredentials: dato subscription-token returned ${datoRes.status}`,
    );
  }
  const { token: datocmsReadToken, environment: datocmsEnvironment } =
    (await datoRes.json()) as { token?: string; environment?: string };
  if (!datocmsReadToken) {
    throw new Error(
      'fetchSpaCredentials: missing token in subscription-token response',
    );
  }

  let openaiKey = '';
  if (openaiRes?.ok) {
    const body = (await openaiRes.json()) as { key?: string };
    if (body.key) {
      openaiKey = body.key;
    } else {
      console.warn(
        'fetchSpaCredentials: openai-key response missing `key` field; AI features will be unavailable',
      );
    }
  } else {
    console.warn(
      `fetchSpaCredentials: openai-key endpoint returned ${openaiRes?.status ?? 'network error'}; AI features will be unavailable until backend rollout completes`,
    );
  }

  setSpaCredentials({
    datocmsReadToken,
    datocmsEnvironment: datocmsEnvironment ?? '',
    openaiKey,
  });
}
